import { useApolloClient } from '@apollo/client'
import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC, PropsWithChildren, useCallback, useState } from 'react'

import CashMultiple from '@Assets/SVG/CashMultiple'
import CheckDecagramOutline from '@Assets/SVG/CheckDecagramOutline'
import SvgClubTrendhim from '@Assets/SVG/ClubTrendhim'
import SvgMembers from '@Assets/SVG/Members'
import { NativeModal, NativeModalBody, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { Wordbook } from '@Components/Wordbook'
import { BlockTypeWithGlobal, PagePresentation, PageType } from '@Generated/graphql'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { getSinglePostByID } from '@Queries/cms/SinglePost.query'
import { Checkbox } from '@Shared/Components/Inputs/Checkbox/Checkbox'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './ClubTerms.module.scss'
import stylesV2 from './ClubTermsV2.module.scss'

export interface ClubTermsProps {
  accepted: boolean
  className?: string
  headerJoin?: boolean
  onAccept: (accepted: boolean) => void
  v2?: boolean
}

const ClubInformations = [
  {
    title: 'common:TRENDHIMCLUB_15_PERCENTAGE_CASHBACK_ALL_ORDERS',
    icon: <CashMultiple />,
  },
  {
    title: 'common:TRENDHIMCLUB_JOIN_X_MEMBERS',
    icon: <SvgMembers />,
  },
  {
    title: 'common:TRENDHIMCLUB_ITS_100_PERCENTAGE_FREE',
    icon: <CheckDecagramOutline />,
  },
]

export const ClubTerms: FC<PropsWithChildren<ClubTermsProps>> = ({ accepted, children, className, headerJoin, onAccept, v2 }) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const modalRef = useNativeModalActions()
  const client = useApolloClient()
  const [pageComponents, setPageComponents] = useState<BlockTypeWithGlobal[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const components = useBlockComponents(pageComponents, '', false, PagePresentation.Modal, false)
  const getPage = useCallback(async (type: PageType, id: string) => {
    modalRef.current?.open()
    setLoading(true)
    const { data } = await getSinglePostByID(client, type, id)
    setPageComponents((data?.page?.blocks ? data.page?.blocks : []) as BlockTypeWithGlobal[])
    setLoading(false)
  }, [client, modalRef])

  if (v2) {
    return (
      <>
        <div className={clsx(stylesV2.clubTerms, cofDesignV1 && stylesV2.cofDesignV1)}>
          <div className={stylesV2.content}>
            <Checkbox
              className={clsx(stylesV2.termsCheckbox, headerJoin && stylesV2.headerJoin)}
              label={(
            headerJoin
              ? (
                <Wordbook
                  components={{
                    TrendhimClubIcon:
                  <SvgClubTrendhim className={stylesV2.trendhimClubFaqSvg} />,
                  }}
                  wordbook={{ defaults: t('common:TRENDHIMCLUB_JOIN_TRENDHIM_CLUB') }}
                />
              )
              : t('common:TRENDHIMCLUB_ACCEPT_TERMS_OF_SERVICE'))}
              setInputValue={onAccept}
              value={accepted}
            />
            <div className={stylesV2.children}>
              {children}
            </div>
          </div>
          <div className={stylesV2.termsOfAccept}>
            <Wordbook
              components={{
                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                LinkPolicy: <button className={stylesV2.termsModalButton} onClick={() => getPage(PageType.Terms, '309')} type="button" />,
                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                LinkService: <button className={stylesV2.termsModalButton} onClick={() => getPage(PageType.Landingpage, '2323')} type="button" />,
              }}
              wordbook={{ defaults: t('common:TRENDHIMCLUB_COF_TERMS_ACCEPT') }}
            />
          </div>
          <div className={stylesV2.clubInfo}>
            {map(ClubInformations, ClubInformation => (
              <div key={`ClubInfo_${ClubInformation.title}`} className={stylesV2.clubInfoItem}>
                {ClubInformation.icon}
                <span className={stylesV2.title}>{t(ClubInformation.title)}</span>
              </div>
            ))}
          </div>
        </div>
        <NativeModal actionRef={modalRef} alignment="right" backdrop closeOnBackDrop closeOnEscape>
          <NativeModalHeader float />
          <NativeModalBody className={stylesV2.modalBody}>
            {loading && <Spinner />}
            {components}
          </NativeModalBody>
        </NativeModal>
      </>
    )
  }

  return (
    <div className={clsx(styles.clubTerms, className)}>
      <Checkbox
        className={clsx(styles.termsCheckbox, headerJoin && styles.headerJoin)}
        label={(
            headerJoin
              ? (
                <Wordbook
                  components={{
                    TrendhimClubIcon:
                  <SvgClubTrendhim className={styles.trendhimClubFaqSvg} />,
                  }}
                  wordbook={{ defaults: t('common:TRENDHIMCLUB_JOIN_TRENDHIM_CLUB') }}
                />
              )
              : t('common:TRENDHIMCLUB_ACCEPT_TERMS_OF_SERVICE')
)}
        setInputValue={onAccept}
        value={accepted}
      />
      {children}
      <Wordbook
        components={{
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          LinkPolicy: <button className={styles.termsModalButtons} onClick={() => getPage(PageType.Terms, '309')} type="button" />,
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          LinkService: <button className={styles.termsModalButtons} onClick={() => getPage(PageType.Landingpage, '2323')} type="button" />,
        }}
        wordbook={{ defaults: t('common:TRENDHIMCLUB_COF_TERMS_ACCEPT') }}
      />
      <NativeModal actionRef={modalRef} alignment="right" backdrop closeOnBackDrop closeOnEscape>
        <NativeModalHeader float />
        <NativeModalBody className={stylesV2.modalBody}>
          {loading && <Spinner />}
          {components}
        </NativeModalBody>
      </NativeModal>
    </div>
  )
}

